// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { Store } from 'antd/lib/form/interface';
import { FormInstance } from 'antd';
import { LineWidgetType, WidgetIntervalType, WidgetOutput } from 'thehive-sdk';
import { SeriesField } from '@Organisms/Dashboard/WidgetManage/SeriesField';
import DividerBasics from '@Organisms/Dashboard/WidgetManage/WidgetFormCommon/DividerBasics';
import TitleField from '@Organisms/Dashboard/WidgetManage/WidgetFormField/TitleField';
import IntervalField from '@Organisms/Dashboard/WidgetManage/WidgetFormField/IntervalField';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { useLocalMessage } from '@Helpers/locale';
import { THForm } from '@Molecules/THForm/THForm';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';

export interface LineValues extends Store {
    title: string;
    interval: WidgetIntervalType;
    series: any[];
    stacked?: boolean;
    log?: boolean;
}

interface LineFormProps {
    form: FormInstance;
    defaultValues?: WidgetOutput & { options: LineWidgetType };
    isValid?: boolean;
    onFinish: (values: LineValues) => void;
    onCancel: () => void;
    onChange: () => void;
}

export const LineForm = ({ form, defaultValues, onFinish, onCancel, onChange }: LineFormProps): ReactElement | null => {
    const i = useLocalMessage();

    return (
        <THForm<LineValues>
            name="line-form"
            form={form}
            onFinish={onFinish}
            onValuesChange={onChange}
            initialValues={{
                title: defaultValues?.options.title,
                interval: defaultValues?.options.interval,
                stacked: defaultValues?.options.stacked,
                log: defaultValues?.options.log,
                series: defaultValues?.options.series?.map((serie) => ({
                    ...serie,
                    periodDateField: serie.periodDateField || '_createdAt',
                    dateField: serie.dateField || '_createdAt',
                })),
            }}
        >
            <THForm.Layout footer={<FormSubmitButton onCancel={onCancel} disabled={false} />}>
                <DividerBasics />

                <TitleField />
                <IntervalField />

                <SeriesField form={form} lineField />

                <THForm.Item name="stacked" label={i('dashboard.stack-type')} valuePropName="checked">
                    <THSwitch />
                </THForm.Item>

                <THForm.Item name="log" label={i('dashboard.log')} valuePropName="checked">
                    <THSwitch />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
